<template>
  <dialog
    ref="dialog"
    class="product-landing-overlay"
    :aria-label="label"
    @cancel.prevent="$emit('close')"
  >
    <header class="product-landing-overlay__header">
      <BaseButton variant="ghost" @click="$emit('close')">
        <span aria-hidden="true">←</span> {{ closeLabel }}
      </BaseButton>
    </header>
    <slot />
  </dialog>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

import BaseButton from '@/shared/components/base/BaseButton.vue'

defineProps<{ label: string; closeLabel: string }>()
defineEmits<{ close: [] }>()

const dialog = ref<HTMLDialogElement | null>(null)
const scrollPosition = { left: window.scrollX, top: window.scrollY }
const previousOverflow = document.body.style.overflow
const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null

onMounted(() => {
  document.body.style.overflow = 'hidden'
  dialog.value?.showModal()
  if (dialog.value) dialog.value.scrollTop = 0
})

onBeforeUnmount(() => {
  dialog.value?.close()
  document.body.style.overflow = previousOverflow
  previousFocus?.focus({ preventScroll: true })
  window.scrollTo({ ...scrollPosition, behavior: 'instant' })
})
</script>

<style scoped lang="scss">
.product-landing-overlay {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100dvh;
  max-width: none;
  max-height: none;
  padding: 0;
  margin: 0;
  border: 0;
  background: var(--ds-surface);
  color: var(--ds-text);
  overflow-y: auto;
  overscroll-behavior: contain;
  scroll-padding-top: 4.5rem;
  animation: product-landing-enter var(--ds-motion-base) var(--ds-ease-game);
}

.product-landing-overlay__header {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: max(var(--ds-space-sm), env(safe-area-inset-top)) var(--ds-space-lg) var(--ds-space-sm);
  background: var(--ds-surface);
  border-bottom: 1px solid var(--ds-border);
}

@keyframes product-landing-enter {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

@media (prefers-reduced-motion: reduce) {
  .product-landing-overlay { animation: none; }
}
</style>
